import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Contact } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { MicrophonePermissionService } from '@app/core/services/microphone-permission.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { AudioType } from '@app/preferences/models/audio-type.enum';
import {
  TTSLanguage,
  TTSVoice,
  VoicemailFileGreeting,
  VoicemailGreeting,
  VoicemailGreetingAudioItem,
  VoicemailSettingPageType,
  VoicemailSettingsModel,
  VoicemailTTSGreeting,
} from '@app/preferences/models/voicemail.model';
import { VoicemailSettingsFacadeService } from '@app/preferences/services/voicemail-settings-facade.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { ButtonType } from '@app/shared/models/dialog-data';
import { Track } from '@app/shared/models/track.model';
import { VoiceMessageRecorder } from '@app/shared/models/voice-message.recorder.model';
import { AudioUtil } from '@app/shared/utils/audio.util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { delay } from 'rxjs';

import { VoicemailChangePinComponent } from './pages/voicemail-change-pin/voicemail-change-pin.component';
import { VoicemailMainPageComponent } from './pages/voicemail-main-page/voicemail-main-page.component';
import { VoicemailManageGreetingComponent } from './pages/voicemail-manage-greeting/voicemail-manage-greeting.component';

@Component({
  selector: 'app-voicemail',
  templateUrl: './voicemail.component.html',
  styleUrls: ['./voicemail.component.scss'],
})
@UntilDestroy()
export class VoicemailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollArea') scrollArea: ElementRef;
  @ViewChild('greyMask') greyMask?: ElementRef<HTMLDivElement>;
  @ViewChild('voicemailMainPageComponent') voicemailMainPageComponent?: VoicemailMainPageComponent;
  @ViewChild('voicemailManageGreetingComponent') voicemailManageGreetingComponent?: VoicemailManageGreetingComponent;
  @ViewChild('voicemailChangePinComponent') voicemailChangePinComponent?: VoicemailChangePinComponent;

  @Output() voicemailSettingsSaved = new EventEmitter();

  protected voiceMessageRecorder: VoiceMessageRecorder;
  private _objectURL?: string;
  private get objectURL(): string | undefined {
    return this._objectURL;
  }
  private set objectURL(url: string) {
    this.tryReleaseObjectURL();
    this._objectURL = url;
  }

  protected selectedPage: VoicemailSettingPageType = 'Main';
  protected voicemailSettings?: VoicemailSettingsModel;
  protected ttsLanguages: TTSLanguage[] = [];
  protected voicesOfSelectedLanguage: TTSVoice[] = [];
  protected greetings: VoicemailGreetingAudioItem[] = [];
  protected recordedName: VoicemailGreetingAudioItem | undefined = undefined;
  protected indexToEdit: number | string = -1;
  protected editAudioType: AudioType = AudioType.greeting;
  protected itemToEdit: VoicemailGreeting | undefined = undefined;
  protected audioToEdit: VoicemailGreetingAudioItem | null = null;
  private playingAudio: AudioUtil | undefined;
  protected playingAudioId: string | null = null;
  protected track: Track | undefined = { title: '', link: '' };
  protected isLoading = true;
  protected currentUser: Contact | undefined;
  protected recordingSeconds: number = 10;
  protected recordedFile: File | null = null;
  private recordingAudio: AudioUtil = new Audio() as AudioUtil;
  private recordingIntervalId: NodeJS.Timeout;
  protected saveButtonEnabled: boolean = false;
  protected isSaving: boolean = false;

  protected editingDisabled = true;

  protected readonly AudioType = AudioType;
  protected readonly BrandingField = BrandingField;

  constructor(
    private facade: VoicemailSettingsFacadeService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private appConfigService: AppConfigService,
    protected contactService: ContactService,
    private microphonePermissionService: MicrophonePermissionService,
    private appTranslateService: AppTranslateService
  ) {
    this.voiceMessageRecorder = new VoiceMessageRecorder(this.microphonePermissionService);
    this.voiceMessageRecorder.setSampleRate(8000);

    this.voiceMessageRecorder.state$.pipe(untilDestroyed(this)).subscribe(async (state) => {
      if (state === 'finished') {
        this.recordedFile = await this.voiceMessageRecorder.getRecordingAsWavFile('UploadedGreeting.wav');

        this.objectURL = URL.createObjectURL(this.recordedFile);
        this.recordingAudio.src = this.objectURL;
        this.track = {
          link: this.recordingAudio.src,
          title: 'RecordedGreeting.wav',
          duration: this.voiceMessageRecorder.duration,
        };
        this.recordingSeconds = 0;
      }
    });
    this.voiceMessageRecorder.elapsedTime$.pipe(untilDestroyed(this)).subscribe((time) => {
      this.recordingSeconds = time;
    });
  }

  ngOnInit() {
    this.editingDisabled = !this.appConfigService.features[AppFeature.ModifyVoicemail];
    this.isLoading = true;
    this.facade
      .getVoicemailSettings$()
      .pipe(untilDestroyed(this))
      .subscribe((settings: VoicemailSettingsModel | undefined) => {
        this.isLoading = false;
        this.voicemailSettings = settings;
      });
    this.facade.getVoicemailSettings();
    this.facade
      .getVoicemailSettingsSaved$()
      .pipe(untilDestroyed(this))
      .subscribe((success) => {
        if (success) {
          this.voicemailSettingsSaved.emit();
        }
      });
    this.facade
      .getVoicemailPinUpdated$()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.setSelectedPage('Main');
      });
    this.facade
      .getTTSLanguages$()
      .pipe(untilDestroyed(this))
      .subscribe((ttsLanguages: TTSLanguage[]) => {
        this.ttsLanguages = ttsLanguages;
      });
    this.facade
      .getTTSVoices$()
      .pipe(untilDestroyed(this))
      .subscribe((voices) => {
        this.voicesOfSelectedLanguage = voices;
      });
    this.facade
      .getAudioUploaded$()
      .pipe(untilDestroyed(this), delay(4500))
      .subscribe(() => {
        this.setSelectedPage('Main');
        this.voicemailSettings = undefined;
        this.facade.getVoicemailSettings();
      });
    this.facade
      .getGreetingList$()
      .pipe(untilDestroyed(this))
      .subscribe((greetings) => {
        this.greetings = greetings;
      });
    this.facade
      .getRecordedName$()
      .pipe(untilDestroyed(this))
      .subscribe((recordedName) => {
        this.recordedName = recordedName;
      });
    this.facade
      .getPlayingAudio$()
      .pipe(untilDestroyed(this))
      .subscribe((audio) => {
        this.playingAudio = audio;
        if (!this.playingAudio) {
          this.playingAudioId = null;
          this.recordingSeconds = 0;
          clearInterval(this.recordingIntervalId);
        }
      });
    this.facade
      .getCurrentUser$()
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.currentUser = user;
      });
    this.facade.getTTSLanguages();
  }

  ngAfterViewInit(): void {
    if (this.editingDisabled) {
      // pass scroll events from grey mask to main container
      this.greyMask?.nativeElement.addEventListener('wheel', (event: WheelEvent) => {
        this.scrollArea.nativeElement.scrollBy(event.deltaX, event.deltaY);
      });

      // block mouse and keyboard events except Escape key and click on Cancel button
      this.scrollArea.nativeElement.addEventListener(
        'click',
        (event) => {
          if (event.target.parentElement.id !== 'cancel-button') {
            this.stopAndPreventDefault(event);
          }
        },
        true
      );
      this.scrollArea.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
          this.stopAndPreventDefault(event);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.voiceMessageRecorder.cancelRecording();
    this.tryReleaseObjectURL();

    this.recordingAudio.pause();
    this.recordingAudio.src = '';
  }

  private stopAndPreventDefault(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  protected setSelectedPage(page: VoicemailSettingPageType) {
    this.selectedPage = page;
    this.stopAudio();
    if (this.selectedPage === 'Main') {
      this.itemToEdit = undefined;
      this.audioToEdit = null;
      this.recordingSeconds = 0;
      this.recordingAudio = new Audio() as AudioUtil;
    }
    this.scrollArea.nativeElement.scrollTop = 0;
  }

  protected goToManageGreetingPage(audioType: AudioType) {
    this.editAudioType = audioType;
    if (this.itemToEdit) {
      this.indexToEdit = audioType === AudioType.name ? 0 : Number(this.itemToEdit.index);
    } else {
      if (this.editAudioType === AudioType.name) {
        this.indexToEdit = 0;
      } else {
        this.indexToEdit =
          this.greetings.length > 0
            ? Number(Math.max(...this.greetings.map((greeting) => Number(greeting.index)))) + 1
            : 1;
      }
    }
    this.track = undefined;
    this.recordedFile = null;
    this.setSelectedPage('ManageGreeting');
  }

  protected async updatePin(pin: string): Promise<void> {
    this.isSaving = true;
    try {
      await this.facade.updatePin(pin);
    } catch {
      this.snackbarService.open(this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.FAILED_TO_UPDATE_PIN'));
    } finally {
      this.isSaving = false;
    }
  }

  public async uploadTTS(greeting: VoicemailTTSGreeting): Promise<void> {
    this.isSaving = true;
    try {
      await this.facade.uploadTTS(greeting, this.indexToEdit, this.editAudioType);
    } catch {
      this.snackbarService.open(this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.FAILED_TO_UPLOAD_GREETING'));
    } finally {
      this.isSaving = false;
    }
  }

  public async uploadAudioFile(greeting: VoicemailFileGreeting): Promise<void> {
    try {
      await this.facade.uploadAudioFile(greeting, this.editAudioType);
    } catch {
      this.snackbarService.open(this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.FAILED_TO_UPLOAD_AUDIO_FILE'));
    } finally {
      this.isSaving = false;
    }
  }

  protected downloadAudioFile(greeting: VoicemailGreetingAudioItem) {
    this.facade.downloadAudioFile(
      greeting.index,
      greeting.type === 'greeting' ? AudioType.greeting : AudioType.name,
      greeting.description
    );
  }

  protected async toggleRecording(audioType: AudioType) {
    this.voiceMessageRecorder.isRecording
      ? this.stopInputSound()
      : this.voiceMessageRecorder.startRecording(audioType === AudioType.greeting ? 60 : 10);
  }

  protected async saveSettings(settings: VoicemailSettingsModel): Promise<void> {
    if (!this.editingDisabled) {
      this.isSaving = true;
      try {
        await this.facade.saveVoicemailSettings(settings);
      } catch {
        this.snackbarService.open(this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.FAILED_TO_SAVE_SETTINGS'));
      } finally {
        this.isSaving = false;
      }
    }
  }

  protected isSaveButtonEnabledChanged(e: boolean): void {
    this.saveButtonEnabled = e;
  }

  protected getVoicesOfLanguage(language: TTSLanguage): void {
    this.facade.getTTSVoiceOfLanguage(language.id);
  }

  protected onAudioDeleteClicked(audioItem: VoicemailGreetingAudioItem): void {
    if (audioItem.type === 'greeting') {
      if (this.voicemailSettings?.confirmDeletion) {
        this.confirmDeleteGreeting(audioItem);
      } else {
        this.deleteGreeting(audioItem);
      }
    } else {
      this.deleteRecordedName();
    }
  }

  protected onGreetingEditClicked(greetingAudioItem: VoicemailGreetingAudioItem): void {
    this.audioToEdit = greetingAudioItem;
    this.itemToEdit = this.voicemailSettings?.greetings.find(
      (greeting) => greeting.index === greetingAudioItem.index.toString()
    );
    this.goToManageGreetingPage(AudioType.greeting);
  }

  protected onManageRecorderNameClicked(): void {
    if (this.recordedName) {
      this.audioToEdit = this.recordedName;
      this.itemToEdit = this.voicemailSettings?.recordedNames;
    }
    this.goToManageGreetingPage(AudioType.name);
  }

  private confirmDeleteGreeting(toBeDeleted: VoicemailGreetingAudioItem): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.DELETE_GREETING_TITLE'),
        description: this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.DELETE_GREETING_CONFIRM'),
        buttons: [
          {
            label: this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.CANCEL'),
            color: 'primary',
            action: () => null,
            type: ButtonType.matStrokedButton,
          },
          {
            label: this.appTranslateService.instant('VOICE_MAIL_PREFERENCES.DELETE'),
            color: 'warn',
            action: () => this.deleteGreeting(toBeDeleted),
            type: ButtonType.matRaisedButton,
            default: true,
          },
        ],
      },
    });
  }

  private deleteGreeting(greetingAudioItem: VoicemailGreetingAudioItem): void {
    this.facade.deleteGreetingAudioItem(greetingAudioItem);
  }

  private deleteRecordedName(): void {
    this.facade.deleteRecordedNameAudioItem();
  }

  protected async playAudio(audio: VoicemailGreetingAudioItem | null) {
    if (audio === null) {
      return;
    }
    this.playingAudioId = audio.type === 'greeting' ? audio.index : 'name';
    const audioUtil: AudioUtil = new Audio(audio.mediaUrl) as AudioUtil;
    await this.facade.playAudio(audioUtil);
  }

  protected stopAudio(): void {
    this.facade.stopAudio();
    this.recordingSeconds = 0;
    clearInterval(this.recordingIntervalId);
  }

  protected playTtsPreview(data: { voice: TTSVoice; text: string }): void {
    this.playingAudioId = 'voice';
    this.facade.previewTts(data.voice, data.text);
  }

  protected async playRecordedFile() {
    if (this.track && this.playingAudioId != 'recorded') {
      const audioUtil: AudioUtil = new Audio(this.track.link) as AudioUtil;
      await this.facade.playAudio(audioUtil);
      this.recordingSeconds = 0;
      this.recordingIntervalId = setInterval(() => this.recordingSeconds++, 1000);
      this.playingAudioId = 'recorded';
    } else {
      this.playingAudioId = null;
      this.recordingSeconds = 0;
      clearInterval(this.recordingIntervalId);
      this.facade.stopAudio();
    }
  }

  private stopInputSound() {
    this.recordingAudio.pause();

    this.voiceMessageRecorder.stopRecording();
  }

  protected async playAudioToEdit() {
    if (this.audioToEdit && this.playingAudioId != this.audioToEdit.index) {
      const audioUtil: AudioUtil = new Audio(this.audioToEdit.mediaUrl) as AudioUtil;
      await this.facade.playAudio(audioUtil);
      this.recordingSeconds = 0;
      this.recordingIntervalId = setInterval(() => this.recordingSeconds++, 1000);
      this.playingAudioId = this.audioToEdit.index;
    } else {
      this.playingAudioId = null;
      this.recordingSeconds = 0;
      clearInterval(this.recordingIntervalId);
      this.facade.stopAudio();
    }
  }

  protected handleCancelButtonClick(selectedPage: string) {
    if (selectedPage === 'Main') {
      this.voicemailSettingsSaved.emit(false);
    } else if (selectedPage === 'ManageGreeting' || selectedPage === 'Pin') {
      this.setSelectedPage('Main');
    }
  }

  protected handleSavePreferencesClick(selectedPage: string) {
    if (selectedPage === 'Main') {
      this.voicemailMainPageComponent?.handleClickOnSave();
    } else if (selectedPage === 'ManageGreeting') {
      this.voicemailManageGreetingComponent?.handleSaveClicked();
    } else if (selectedPage === 'Pin') {
      this.voicemailChangePinComponent?.save();
    }
  }

  private tryReleaseObjectURL() {
    if (this._objectURL) {
      URL.revokeObjectURL(this._objectURL);
      this._objectURL = undefined;
    }
  }
}
